import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, UtensilsCrossed, Music, Mic, Sparkles, PartyPopper,
  Camera, Truck, Plane, ArrowRight, Phone, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CATEGORIES, SERVICE_AREA } from "@/lib/eventData";
import ChatWidget from "@/components/ChatWidget";

const ICONS = { MapPin, UtensilsCrossed, Music, Mic, Sparkles, PartyPopper, Camera, Truck, Plane };

const HERO_IMG =
  "https://images.unsplash.com/photo-1545128485-c400e7702796?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxmdW4lMjBldmVudCUyMHBhcnR5JTIwcGVvcGxlfGVufDB8fHx8MTc5MTE3NzYyM3ww&ixlib=rb-4.1.0&q=85";
const NICK_IMG = "/nick.jpg";
const FOOD_IMG =
  "https://images.unsplash.com/photo-1536392706976-e486e2ba97af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwZGlubmVyJTIwdGFibGUlMjBzZXR1cHxlbnwwfHx8fDE3OTExNzc2MjN8MA&ixlib=rb-4.1.0&q=85";
const DJ_IMG =
  "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxkaiUyMHBhcnR5JTIwY3Jvd2R8ZW58MHx8fHwxNzkxMTc3NjIzfDA&ixlib=rb-4.1.0&q=85";

const WHAT_NICK_DOES = [
  "Venues", "Food & Catering", "Cake", "DJs & Karaoke", "Live Music", "Hosts & MCs",
  "Games, Bingo & Lotería", "Character Performers", "Balloons & Decor", "Photo & Video",
  "Photo Booths", "Piñatas", "Transportation", "Hotels", "Setup & Cleanup", "Event Hosting",
];

export default function Home() {
  return (
    <div className="min-h-screen bg-nick-bg text-nick-dark">
      {/* NAV */}
      <header className="sticky top-0 z-50 nick-glass border-b border-black/5">
        <div className="mx-auto max-w-6xl px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="font-display font-extrabold text-xl tracking-tight">
            Let's Party <span className="text-nick-primary">Houston</span>
          </Link>
          <div className="flex items-center gap-2">
            <a
              href="#categories"
              className="hidden sm:inline-flex text-sm font-semibold text-nick-muted hover:text-nick-dark transition-colors px-3 py-2"
            >
              What Nick Plans
            </a>
            <Link to="/plan" data-testid="nav-plan-btn">
              <Button className="rounded-full bg-nick-primary hover:bg-nick-primary-hover text-white font-semibold">
                Plan My Event
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="People celebrating at a party" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-nick-dark/60" />
        </div>
        <div className="relative mx-auto max-w-6xl px-5 py-24 sm:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-2xl"
          >
            <span className="overline text-white/80">Houston & Surrounding Areas</span>
            <h1 className="mt-4 font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Plan your event <span className="text-nick-primary">with Nick</span>
            </h1>
            <p className="mt-6 text-lg text-white/90 max-w-xl leading-relaxed">
              Tell us what you're celebrating, what you want, and where you're having it. Nick will help put the pieces together.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/plan" data-testid="hero-plan-btn">
                <Button
                  size="lg"
                  className="w-full sm:w-auto rounded-full bg-nick-primary hover:bg-nick-primary-hover text-white font-bold text-base px-8 h-14 group"
                >
                  Plan My Event
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="#categories" data-testid="hero-see-btn">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto rounded-full bg-white/10 border-white/40 text-white hover:bg-white hover:text-nick-dark font-bold text-base px-8 h-14 backdrop-blur-sm"
                >
                  See What Nick Can Plan
                </Button>
              </a>
            </div>
            <p className="mt-8 text-white/80 font-display font-semibold tracking-wide">
              You dream it. Nick makes it happen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="nick-grain">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <div className="max-w-2xl">
            <span className="overline text-nick-primary">What Nick Can Plan</span>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
              One call. Everything handled.
            </h2>
            <p className="mt-3 text-nick-muted text-lg">
              You don't need to know event-planning lingo. Just pick what sounds fun — or let Nick decide.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORIES.map((cat, i) => {
              const Icon = ICONS[cat.icon];
              return (
                <motion.div
                  key={cat.key}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                  data-testid={`category-card-${i}`}
                  className="group rounded-2xl bg-white border border-nick-border p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
                >
                  <div className="h-12 w-12 rounded-xl bg-nick-primary/10 text-nick-primary flex items-center justify-center group-hover:bg-nick-primary group-hover:text-white transition-colors">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-display font-bold text-lg">{cat.key}</h3>
                  <p className="mt-1.5 text-sm text-nick-muted leading-relaxed">
                    {cat.items.slice(0, 5).join(" · ")}
                    {cat.items.length > 5 ? " · and more" : ""}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENTO: imagery + what nick does */}
      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-3xl overflow-hidden relative min-h-[320px] border border-nick-border">
            <img src={DJ_IMG} alt="DJ and party crowd" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-nick-dark/80 via-nick-dark/20 to-transparent" />
            <div className="relative p-8 flex h-full items-end min-h-[320px]">
              <div>
                <span className="overline text-white/80">Music · Entertainment · Vibes</span>
                <h3 className="mt-2 font-display font-extrabold text-2xl sm:text-3xl text-white">
                  Keep the energy going all night.
                </h3>
              </div>
            </div>
          </div>
          <div className="rounded-3xl bg-nick-secondary text-white p-8 flex flex-col justify-between min-h-[320px]">
            <div>
              <span className="overline text-white/70">Everything Nick Handles</span>
              <div className="mt-4 flex flex-wrap gap-2">
                {WHAT_NICK_DOES.map((w) => (
                  <span key={w} className="text-xs font-semibold bg-white/10 rounded-full px-3 py-1.5">
                    {w}
                  </span>
                ))}
              </div>
            </div>
            <Link to="/plan" className="mt-6">
              <Button className="rounded-full bg-white text-nick-secondary hover:bg-nick-bg font-bold w-full">
                Start Planning <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* NICK */}
      <section className="bg-white border-y border-nick-border">
        <div className="mx-auto max-w-6xl px-5 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="rounded-3xl overflow-hidden border border-nick-border max-w-sm">
              <img src={NICK_IMG} alt="Nick, your event host" className="w-full h-full object-cover aspect-[4/5]" />
            </div>
            <div className="absolute -bottom-5 left-6 bg-nick-primary text-white rounded-2xl px-5 py-3 shadow-lg">
              <div className="font-display font-extrabold text-lg">Nick</div>
              <div className="text-xs text-white/85">Your planner & host</div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="overline text-nick-primary">Nick as Your Host</span>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
              Someone to call when you don't know where to start.
            </h2>
            <p className="mt-4 text-nick-muted text-lg leading-relaxed">
              Need someone to keep the party moving? Nick can host, make announcements, run games, manage karaoke,
              interact with guests and help make sure your event keeps its energy.
            </p>
            <ul className="mt-6 space-y-3">
              {["Friendly, fun & professional", "Up to 4 hours hosting with Full Service", "Based in Houston, serving the whole metro"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-nick-dark font-medium">
                  <span className="h-6 w-6 rounded-full bg-nick-primary/10 text-nick-primary flex items-center justify-center">
                    <Check className="h-4 w-4" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FOOD / SERVICE AREA */}
      <section className="mx-auto max-w-6xl px-5 py-20 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-3xl overflow-hidden border border-nick-border min-h-[260px] relative">
          <img src={FOOD_IMG} alt="Elegant event table setup" className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <div className="rounded-3xl bg-white border border-nick-border p-8">
          <span className="overline text-nick-primary">Service Area</span>
          <h3 className="mt-3 font-display font-extrabold text-2xl">Houston & surrounding areas</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {SERVICE_AREA.map((c) => (
              <span key={c} className="text-sm font-semibold bg-nick-bg border border-nick-border rounded-full px-3 py-1.5">
                {c}
              </span>
            ))}
          </div>
          <p className="mt-5 text-sm text-nick-muted leading-relaxed">
            Outside the primary area? Nick may be able to travel to your event — travel fees may apply.
            Prices shown may be estimates until vendors confirm availability and pricing.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="rounded-3xl bg-nick-dark text-white p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 nick-grain opacity-40" />
          <div className="relative">
            <h2 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight">
              You dream it. <span className="text-nick-primary">Nick makes it happen.</span>
            </h2>
            <p className="mt-4 text-white/80 text-lg max-w-xl mx-auto">
              Describe your event in a few minutes and get a preliminary budget. No pressure, no planning lingo.
            </p>
            <Link to="/plan" data-testid="cta-plan-btn">
              <Button
                size="lg"
                className="mt-8 rounded-full bg-nick-primary hover:bg-nick-primary-hover text-white font-bold text-base px-10 h-14"
              >
                Plan My Event <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-nick-border">
        <div className="mx-auto max-w-6xl px-5 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="font-display font-extrabold text-lg">
            Let's Party <span className="text-nick-primary">Houston</span>
          </div>
          <p className="text-sm text-nick-muted flex items-center gap-2">
            <Phone className="h-4 w-4" /> Serving Houston, TX & surrounding areas
          </p>
          <Link to="/admin/login" data-testid="footer-admin-link" className="text-sm text-nick-muted hover:text-nick-dark transition-colors">
            Nick's Login
          </Link>
        </div>
      </footer>

      <ChatWidget />
    </div>
  );
}
